import { useCallback, useEffect, useMemo, useState } from "react";
import { createDatasetAssistantApi } from "datasets/components/assistant/api/DatasetAssistantApi";
import useDataset from "datasets/hooks/useDataset";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import SkillEditorModal, {
  type SkillFormData,
  type SkillTag,
} from "~/components/assistant/SkillEditorModal";
import type { PartialSkill, Skill } from "~/components/assistant/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Switch } from "~/components/ui/switch";
import useErrorToast from "~/contexts/error-toast";
import rumClient from "~/utils/monitoring";

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;
const api = createDatasetAssistantApi(VITE_ANALYSIS_AGENT_API);

const DatasetSkillsTab = () => {
  const { data: dataset } = useDataset();
  const { showError } = useErrorToast();
  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;

  const [skills, setSkills] = useState<PartialSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [submitting, setSubmitting] = useState<string | null>(null);

  // Editor modal state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);

  // Delete confirmation state
  const [deletingSkillId, setDeletingSkillId] = useState<string | null>(null);

  const datasetId = dataset?.id;

  const loadSkills = useCallback(async () => {
    if (!datasetId) return;
    try {
      const response = await api.listSkills(datasetId);
      setSkills(response.dataset);
      setFetchError(false);
    } catch (e) {
      console.error("Failed to load dataset skills:", e);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [datasetId]);

  useEffect(() => {
    loadSkills();
  }, [loadSkills]);

  const handleToggle = async (skillId: string, enabled: boolean) => {
    if (submitting) return;
    setSubmitting(skillId);

    try {
      await api.updateSkill(skillId, { enabled });
      setSkills((prev) => prev.map((s) => (s.id === skillId ? { ...s, enabled } : s)));
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to update skill");
    } finally {
      setSubmitting(null);
    }
  };

  const handleAddSkill = () => {
    setEditingSkill(null);
    setEditorOpen(true);
  };

  const handleEditSkill = async (skillId: string) => {
    try {
      const skill = await api.getSkill(skillId);
      setEditingSkill(skill);
      setEditorOpen(true);
    } catch (e) {
      console.error(e);
      showError("Unable to load skill for editing");
    }
  };

  const handleEditorSubmit = async (data: SkillFormData) => {
    if (!dataset) return;
    setEditorLoading(true);

    try {
      if (editingSkill) {
        await api.updateSkill(editingSkill.id, {
          description: data.description,
          content: data.content,
          tags: data.tags,
        });
      } else {
        await api.createSkill({
          name: data.name,
          description: data.description,
          content: data.content,
          tags: data.tags,
          scope: "DATASET",
          scope_id: dataset.id,
        });
      }
      setEditorOpen(false);
      setEditingSkill(null);
      await loadSkills();
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError(e instanceof Error ? e.message : "Unable to save skill");
    } finally {
      setEditorLoading(false);
    }
  };

  const editorInitialData = useMemo(
    () =>
      editingSkill
        ? {
            name: editingSkill.name,
            description: editingSkill.description ?? "",
            content: editingSkill.content,
            tags: (editingSkill.tags ?? []) as SkillTag[],
          }
        : undefined,
    [editingSkill]
  );

  const handleDeleteSkill = async () => {
    if (!deletingSkillId) return;
    setSubmitting(deletingSkillId);
    try {
      await api.deleteSkill(deletingSkillId);
      setSkills((prev) => prev.filter((s) => s.id !== deletingSkillId));
      setDeletingSkillId(null);
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to delete skill");
    } finally {
      setSubmitting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <p className="text-sm text-destructive">
        Failed to load dataset skills. Please try refreshing the page.
      </p>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          Skills provide domain-specific instructions and workflows for AI Analysis conversations on
          this dataset.
        </p>

        {isOwner && (
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={handleAddSkill} aria-label="Add skill">
              <Plus className="size-4" />
              Add Skill
            </Button>
          </div>
        )}

        {skills.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">
            No dataset skills yet. Add skills to customize AI behavior for this dataset.
          </p>
        ) : (
          <div className="flex flex-col gap-1">
            {skills.map((skill) => (
              <div key={skill.id} className="flex items-center justify-between py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{skill.name}</p>
                  {skill.description && (
                    <p className="truncate text-[13px] text-muted-foreground">
                      {skill.description}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  {isOwner && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        onClick={() => handleEditSkill(skill.id)}
                        aria-label={`Edit ${skill.name}`}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        onClick={() => setDeletingSkillId(skill.id)}
                        aria-label={`Delete ${skill.name}`}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </>
                  )}
                  <Switch
                    checked={skill.enabled}
                    disabled={!isOwner || submitting === skill.id}
                    onCheckedChange={(checked) => handleToggle(skill.id, checked)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!deletingSkillId} onOpenChange={() => setDeletingSkillId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Skill?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;
              {skills.find((s) => s.id === deletingSkillId)?.name}
              &rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSkill}
              disabled={!!submitting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {submitting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SkillEditorModal
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditingSkill(null);
        }}
        onSubmit={handleEditorSubmit}
        loading={editorLoading}
        isEditing={!!editingSkill}
        initialData={editorInitialData}
      />
    </>
  );
};

export default DatasetSkillsTab;
